import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12 lg:py-20">
        <p className="text-[#9A7B4F] text-[11px] tracking-[0.3em] uppercase mb-3 text-center">Legal</p>
        <h1 className="text-3xl lg:text-4xl text-[#1A1A1A] text-center mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }} data-testid="privacy-policy-title">
          Privacy Policy
        </h1>
        <p className="text-[13px] text-[#676664]/60 text-center mb-12">Last updated: February 2026</p>

        <div className="prose-mahra space-y-8 text-[#3A3A3A]" data-testid="privacy-policy-content">
          <Section title="1. Introduction">
            <p>
              Mahra ("we", "us", "our") operates shopmahra.com.au and provides fashion rental services across Australia.
              We are committed to protecting the privacy of our customers and website visitors. This Privacy Policy explains
              how we collect, use, disclose, and safeguard your personal information in accordance with the Australian Privacy
              Act 1988 (Cth) and the Australian Privacy Principles (APPs).
            </p>
            <p>
              By using our website or services, you consent to the practices described in this policy.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p>We may collect the following types of personal information:</p>
            <ul>
              <li><strong>Identity Information:</strong> Full name, date of birth (if provided).</li>
              <li><strong>Contact Information:</strong> Email address, phone number, residential or delivery address, postcode.</li>
              <li><strong>Transaction Information:</strong> Booking history, rental details, payment information (processed securely via Stripe — we do not store full card numbers).</li>
              <li><strong>Technical Information:</strong> IP address, browser type, device information, cookies, and browsing behaviour on our website.</li>
              <li><strong>Communication Information:</strong> Records of correspondence with us, including emails, messages, and feedback.</li>
              <li><strong>Account Information:</strong> Login credentials (email and hashed password), saved preferences, and wish lists.</li>
            </ul>
          </Section>

          <Section title="3. How We Collect Information">
            <p>We collect personal information:</p>
            <ul>
              <li>Directly from you when you create an account, make a booking, complete a purchase, or contact us.</li>
              <li>Automatically through cookies, analytics tools, and similar technologies when you use our website.</li>
              <li>From third-party services such as payment processors (Stripe), email providers (SendGrid), and social media platforms when you interact with us through those channels.</li>
            </ul>
          </Section>

          <Section title="4. How We Use Your Information">
            <p>We use your personal information to:</p>
            <ul>
              <li>Process and manage your rental bookings, deliveries, and returns.</li>
              <li>Process payments and refunds, including security bond handling.</li>
              <li>Send transactional communications (booking confirmations, reminders, return instructions).</li>
              <li>Provide customer support and respond to your enquiries.</li>
              <li>Improve our website, products, and services through analytics.</li>
              <li>Send promotional communications where you have opted in (you may unsubscribe at any time).</li>
              <li>Comply with legal obligations and protect our legitimate interests.</li>
            </ul>
          </Section>

          <Section title="5. Disclosure of Your Information">
            <p>We may share your personal information with:</p>
            <ul>
              <li><strong>Payment Processors:</strong> Stripe processes your payments securely. Your card details are handled directly by Stripe and are not stored on our servers.</li>
              <li><strong>Delivery Partners:</strong> Australia Post or local couriers to facilitate deliveries and returns.</li>
              <li><strong>Email Service Providers:</strong> SendGrid for transactional and promotional emails.</li>
              <li><strong>Cloud Hosting Providers:</strong> For secure data storage and website hosting.</li>
              <li><strong>Legal Authorities:</strong> Where required by law, regulation, or legal proceedings.</li>
            </ul>
            <p>We do not sell, rent, or trade your personal information to third parties for their marketing purposes.</p>
          </Section>

          <Section title="6. Data Security">
            <p>
              We take reasonable steps to protect your personal information from misuse, interference, loss, unauthorised access,
              modification, or disclosure. Our security measures include:
            </p>
            <ul>
              <li>SSL/TLS encryption for all data transmitted between your browser and our servers.</li>
              <li>Secure payment processing through Stripe (PCI DSS compliant).</li>
              <li>Access controls limiting employee access to personal information on a need-to-know basis.</li>
              <li>Regular security reviews and updates to our systems.</li>
            </ul>
            <p>
              While we strive to protect your personal information, no method of electronic transmission or storage is 100% secure.
              We cannot guarantee absolute security.
            </p>
          </Section>

          <Section title="7. Cookies and Tracking">
            <p>
              Our website uses cookies and similar technologies to enhance your browsing experience, remember your preferences,
              and analyse website usage. Types of cookies we use include:
            </p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for the website to function (e.g., session management, cart functionality).</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website.</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and choices (e.g., saved items, event dates).</li>
            </ul>
            <p>
              You can manage cookie preferences through your browser settings. Disabling certain cookies may affect website functionality.
            </p>
          </Section>

          <Section title="8. Your Rights">
            <p>Under the Australian Privacy Principles, you have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request access to the personal information we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate, out-of-date, incomplete, or misleading information.</li>
              <li><strong>Complaint:</strong> Lodge a complaint if you believe we have breached the APPs.</li>
              <li><strong>Opt-out:</strong> Unsubscribe from promotional communications at any time.</li>
            </ul>
            <p>
              To exercise any of these rights, please <a href="/contact" className="text-[#9A7B4F] underline underline-offset-2">contact us</a>.
              We will respond to your request within 30 days.
            </p>
          </Section>

          <Section title="9. Data Retention">
            <p>
              We retain your personal information only for as long as necessary to fulfil the purposes for which it was collected,
              including to satisfy legal, accounting, or reporting requirements. Booking and transaction data is typically retained
              for seven (7) years in accordance with Australian tax and business record-keeping obligations. You may request
              deletion of your account and associated data at any time, subject to our legal obligations.
            </p>
          </Section>

          <Section title="10. Third-Party Links">
            <p>
              Our website may contain links to third-party websites or services (e.g., Instagram, social media platforms).
              We are not responsible for the privacy practices or content of these external sites. We encourage you to
              review their privacy policies before providing any personal information.
            </p>
          </Section>

          <Section title="11. Children's Privacy">
            <p>
              Our services are not directed at individuals under the age of 18. We do not knowingly collect personal
              information from children. If you believe a child has provided us with personal information, please
              contact us so we can take appropriate action.
            </p>
          </Section>

          <Section title="12. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an
              updated "Last updated" date. We encourage you to review this policy periodically. Continued use of
              our website or services after changes constitutes acceptance of the updated policy.
            </p>
          </Section>

          <Section title="13. Contact Us">
            <p>
              If you have any questions, concerns, or complaints about this Privacy Policy or our handling of your
              personal information, please contact us:
            </p>
            <ul>
              <li><strong>Contact:</strong> <a href="/contact" className="text-[#9A7B4F] underline underline-offset-2">Get in touch via our contact form</a></li>
              <li><strong>Location:</strong> Oakleigh South, VIC, Australia</li>
            </ul>
            <p>
              If you are unsatisfied with our response, you may lodge a complaint with the{" "}
              <a href="https://www.oaic.gov.au/" target="_blank" rel="noopener noreferrer" className="text-[#9A7B4F] underline underline-offset-2">
                Office of the Australian Information Commissioner (OAIC)
              </a>.
            </p>
          </Section>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-lg text-[#1A1A1A] font-medium mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{title}</h2>
      <div className="text-[14px] leading-[1.85] space-y-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_li]:text-[14px] [&_li]:leading-[1.75]">
        {children}
      </div>
    </section>
  );
}
